import { useEffect, useState } from 'react';
import { Link, Navigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { fetchPublicProducts, type PublicProduct } from '@/components/subscriptions/lib/public-products-api';
import { getStartedHref, resolveWelcomeRedirect } from '@/components/subscriptions/lib/public-product-catalog';
import { userHasAppAccess } from '@/lib/access-state';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Loader2 } from 'lucide-react';
import logger from '@/lib/logging';

function formatPrice(amount: number | null, currency: string | null, interval: string | null): string {
  if (amount == null) {
    return 'Contact us';
  }
  const formatted = new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: (currency || 'usd').toUpperCase(),
  }).format(amount / 100);
  if (!interval || interval === 'one_time') {
    return formatted;
  }
  return `${formatted} / ${interval}`;
}

function ProductOffering({ product, isAuthenticated }: { product: PublicProduct; isAuthenticated: boolean }) {
  return (
    <Card className="border-2">
      <CardHeader>
        <CardTitle>{product.name}</CardTitle>
        <CardDescription>{product.description}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {product.prices.map((price) => (
          <div key={price.priceId} className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-lg font-medium">
              {formatPrice(price.amount, price.currency, price.interval)}
            </p>
            <Button asChild size="lg">
              <Link to={getStartedHref(price.priceId, isAuthenticated)}>Get started</Link>
            </Button>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}

export function WelcomePage() {
  const { isAuthenticated, loading: authLoading, serverUser } = useAuth();
  const [searchParams] = useSearchParams();
  const checkoutCanceled = searchParams.get('checkout') === 'canceled';
  const browseIntent = searchParams.get('intent') === 'browse-plans';
  const [products, setProducts] = useState<PublicProduct[]>([]);
  const [catalogLoading, setCatalogLoading] = useState(true);
  const [catalogError, setCatalogError] = useState<string | null>(null);

  // `loading` can flip false moments before the server session (and its roles)
  // lands — wait for both so a role-holder isn't shown the catalog by mistake.
  const awaitingServerSession = isAuthenticated && !authLoading && !serverUser;
  const redirectTo =
    !authLoading && !awaitingServerSession
      ? resolveWelcomeRedirect({
          isAuthenticated,
          hasAppAccess: userHasAppAccess(serverUser?.roles ?? []),
          checkoutCanceled,
          browseIntent,
        })
      : null;

  useEffect(() => {
    let cancelled = false;

    async function loadCatalog() {
      setCatalogLoading(true);
      setCatalogError(null);
      try {
        const catalog = await fetchPublicProducts();
        if (!cancelled) {
          setProducts(catalog);
        }
      } catch (error) {
        logger.error('[WelcomePage] Failed to load public catalog:', error);
        if (!cancelled) {
          setCatalogError('Unable to load subscription offerings right now.');
        }
      } finally {
        if (!cancelled) {
          setCatalogLoading(false);
        }
      }
    }

    loadCatalog();
    return () => {
      cancelled = true;
    };
  }, []);

  if (authLoading || awaitingServerSession) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (redirectTo) {
    return <Navigate to={redirectTo} replace />;
  }

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-background p-4">
      <div className="w-full max-w-3xl space-y-6">
        <div className="text-center space-y-2">
          <h1 className="text-4xl font-bold tracking-tight">Welcome to Harry</h1>
          <p className="text-lg text-muted-foreground">
            Biomechanics coaching tools for practitioners who want clarity, not guesswork.
          </p>
        </div>

        {checkoutCanceled ? (
          <Alert variant="destructive">
            <AlertTitle>Checkout canceled</AlertTitle>
            <AlertDescription>
              Your payment was not completed. Choose a plan below to try again.
            </AlertDescription>
          </Alert>
        ) : null}

        {catalogLoading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : null}

        {!catalogLoading && catalogError ? (
          <Alert variant="destructive">
            <AlertTitle>Catalog unavailable</AlertTitle>
            <AlertDescription>{catalogError}</AlertDescription>
          </Alert>
        ) : null}

        {!catalogLoading && !catalogError && products.length === 0 ? (
          <Card>
            <CardContent className="py-8 text-center text-muted-foreground">
              No subscription offerings are available yet. Check back soon.
            </CardContent>
          </Card>
        ) : null}

        {!catalogLoading && !catalogError
          ? products.map((product) => (
              <ProductOffering key={product.productId} product={product} isAuthenticated={isAuthenticated} />
            ))
          : null}

        {!isAuthenticated ? (
          <div className="text-center">
            <Button variant="link" asChild>
              <Link to="/login">Already have an account? Sign in</Link>
            </Button>
          </div>
        ) : null}
      </div>
    </div>
  );
}
